import { useCallback, useState } from 'react'

type WebRefs = {
  containerRef: React.RefObject<HTMLDivElement>
  contentRef: React.RefObject<HTMLDivElement>
}

interface UseVerticalOverflowProps {
  /** Refs for web container and content elements */
  webRefs?: WebRefs
}

/**
 * A hook that detects if content vertically overflows its container on web.
 *
 * Compares container clientHeight against content scrollHeight
 *
 * @example
 * ```tsx
 * // Web
 * const containerRef = useRef<HTMLDivElement>(null)
 * const contentRef = useRef<HTMLDivElement>(null)
 *
 * const { isVerticallyOverflowing, checkVerticalOverflow } = useVerticalOverflow({
 *   webRefs: { containerRef, contentRef }
 * })
 *
 * // In JSX:
 * <Container ref={containerRef}>
 *   <Content ref={contentRef} />
 * </Container>
 * ```
 */
export const useVerticalOverflow = ({ webRefs }: UseVerticalOverflowProps) => {
  const [isVerticallyOverflowing, setIsVerticallyOverflowing] = useState(false)

  const checkVerticalOverflow = useCallback(() => {
    if (webRefs?.containerRef.current && webRefs?.contentRef.current) {
      const containerHeight = webRefs.containerRef.current.clientHeight
      const computedStyle = window.getComputedStyle(webRefs.containerRef.current)
      const containerTopPadding = parseInt(computedStyle.paddingTop, 10)

      // Get the maximum height by comparing different height measurements to handle edge cases:
      // - scrollHeight: includes overflow content and padding
      // - offsetHeight: includes padding and border
      // - getBoundingClientRect().height: includes transforms and gives fractional values
      const contentHeight =
        Math.max(
          webRefs.contentRef.current.scrollHeight,
          webRefs.contentRef.current.offsetHeight,
          webRefs.contentRef.current.getBoundingClientRect().height,
        ) + containerTopPadding // include container top padding as it pushes content down
      setIsVerticallyOverflowing(contentHeight > containerHeight)
    }
  }, [webRefs?.containerRef, webRefs?.contentRef])

  return {
    isVerticallyOverflowing,
    checkVerticalOverflow,
    // These are only included for compatibility with the native version
    onContainerLayout: undefined,
    onContentLayout: undefined,
  }
}
